"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { STANDARD_CLOSE_BUTTON_CLASSNAME } from "@/components/ui/close-button";

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  description?: string;
  showCloseButton?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, description, showCloseButton = true, ...props }, ref) => {
  const descriptionId = React.useId();
  const hasCustomAriaDescribedBy = props["aria-describedby"] !== undefined;
  const shouldRenderDescription = Boolean(description) && !hasCustomAriaDescribedBy;
  const ariaDescribedBy = hasCustomAriaDescribedBy
    ? props["aria-describedby"]
    : shouldRenderDescription
      ? descriptionId
      : undefined;

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "nitfisio-dialog pointer-events-auto fixed left-[50%] top-[50%] z-[60] grid w-[min(94vw,42rem)] max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-2xl border bg-card p-5 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:p-6 md:w-full",
          className,
        )}
        {...props}
        aria-describedby={ariaDescribedBy}
      >
        {shouldRenderDescription && (
          <DialogPrimitive.Description
            id={descriptionId}
            className="mb-4 text-sm text-muted-foreground"
          >
            {description}
          </DialogPrimitive.Description>
        )}
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className={cn("absolute right-4 top-4", STANDARD_CLOSE_BUTTON_CLASSNAME)}>
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
DialogContent.displayName = "DialogContent";

export { DialogContent };
